/**
 * VISTOERA_AI_COST_PRICING_PROVIDER_COST_POINTS_REVENUE_MARGIN_CONTROL_
 * PLANE_P0_V1 — pure financial calculation helpers for the Platform Admin
 * "AI 成本与定价" Drawer live preview.
 *
 * MUST stay byte-identical in formula/rounding semantics with
 * ``backend/app/saas/services/ai_pricing_service.py::compute_financials``
 * (task spec §17-§21). The Drawer calls these helpers client-side so an
 * Admin sees the same revenue / profit / margin numbers the server will
 * return after save, without a round-trip. Any change here MUST be
 * mirrored there (and vice versa) — see
 * ``scripts/aiPricingMarginCalculation.node-test.ts``.
 *
 * Currency guard (task spec §15/§20 hard rule): never hardcode a USD/CNY
 * FX rate. A cost currency that differs from the reference currency
 * degrades to ``CURRENCY_MISMATCH`` ("待汇率") instead of computing a
 * wrong number.
 */

export type AiPricingFinancialStatus =
  | 'OK'
  | 'NO_REFERENCE_POINT_VALUE'
  | 'CURRENCY_MISMATCH'
  | 'NO_COST'
  | 'NO_POINTS'

export interface AiPricingFinancialResult {
  revenuePerUnit: number | null
  grossProfitPerUnit: number | null
  /** Fraction, e.g. 0.5 == 50%. */
  grossMargin: number | null
  status: AiPricingFinancialStatus
}

const MONEY_QUANT = 1_000_000

function toNumber(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : null
}

function quantizeMoney(n: number): number {
  return Math.round(n * MONEY_QUANT) / MONEY_QUANT
}

export function computeFinancials(input: {
  pointsPerUnit: string | number | null | undefined
  estimatedCostPerUnit: string | number | null | undefined
  costCurrency: string | null | undefined
  referencePointValue: string | number | null | undefined
  referenceCurrency: string | null | undefined
}): AiPricingFinancialResult {
  const referencePointValue = toNumber(input.referencePointValue)
  if (referencePointValue === null) {
    return { revenuePerUnit: null, grossProfitPerUnit: null, grossMargin: null, status: 'NO_REFERENCE_POINT_VALUE' }
  }

  const pointsPerUnit = toNumber(input.pointsPerUnit)
  if (pointsPerUnit === null) {
    return { revenuePerUnit: null, grossProfitPerUnit: null, grossMargin: null, status: 'NO_POINTS' }
  }

  const revenue = quantizeMoney(pointsPerUnit * referencePointValue)

  const estimatedCostPerUnit = toNumber(input.estimatedCostPerUnit)
  if (estimatedCostPerUnit === null) {
    return { revenuePerUnit: revenue, grossProfitPerUnit: null, grossMargin: null, status: 'NO_COST' }
  }

  const costCurrency = (input.costCurrency || '').toUpperCase()
  const referenceCurrency = (input.referenceCurrency || '').toUpperCase()
  if (costCurrency && referenceCurrency && costCurrency !== referenceCurrency) {
    return { revenuePerUnit: revenue, grossProfitPerUnit: null, grossMargin: null, status: 'CURRENCY_MISMATCH' }
  }

  const profit = quantizeMoney(revenue - estimatedCostPerUnit)
  const margin = revenue !== 0 ? profit / revenue : null
  return { revenuePerUnit: revenue, grossProfitPerUnit: profit, grossMargin: margin, status: 'OK' }
}

/** Visual hint only (task spec §20) — never persisted / never a DB enum. */
export type MarginTone = 'green' | 'orange' | 'yellow' | 'red' | 'neutral'

export function marginTone(margin: number | null | undefined): MarginTone {
  if (margin === null || margin === undefined || !Number.isFinite(margin)) return 'neutral'
  if (margin < 0) return 'red'
  if (margin < 0.2) return 'yellow'
  if (margin < 0.5) return 'orange'
  return 'green'
}

export function formatMoneyAmount(
  amount: string | number | null | undefined,
  currency: string | null | undefined,
): string {
  const n = toNumber(amount)
  if (n === null) return '—'
  const cur = (currency || '').toUpperCase()
  const symbol = cur === 'USD' ? '$' : cur === 'CNY' || cur === 'RMB' ? '¥' : cur ? `${cur} ` : ''
  const absStr = Math.abs(n).toFixed(6).replace(/\.?0+$/, '')
  const body = absStr.includes('.') ? absStr : `${absStr}.00`
  return n < 0 ? `-${symbol}${body}` : `${symbol}${body}`
}

export function formatMarginPercent(margin: number | null | undefined): string {
  if (margin === null || margin === undefined || !Number.isFinite(margin)) return '—'
  return `${(margin * 100).toFixed(1)}%`
}

/** UI-only integer display for the「积分消耗」column (TASK-00003).
 * Never mutates API/DB values — ``5.0000`` renders as ``"5"``, not ``"5 pts"``. */
export function formatPointConsumption(
  pointsPerUnit: string | number | null | undefined,
): string {
  const n = toNumber(pointsPerUnit)
  if (n === null) return '—'
  return String(Math.trunc(n))
}

export function financialStatusLabelZh(status: AiPricingFinancialStatus): string {
  switch (status) {
    case 'OK':
      return '已计算'
    case 'NO_REFERENCE_POINT_VALUE':
      return '未设置积分参考价值'
    case 'CURRENCY_MISMATCH':
      return '待汇率'
    case 'NO_COST':
      return '未配置供应商成本'
    case 'NO_POINTS':
      return '未配置积分消耗'
    default:
      return status
  }
}
